import styled from '@emotion/styled'
import { Mediatype } from '@pubstack/common/src/adFormat'
import { noLeadingAndTrailingSpace } from '@pubstack/common/src/input'
import { AdUnitEnv, isValidAdUnitEnv, PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { FunctionComponent, useEffect } from 'react'
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdUnit, useCreateAdUnit, useUpdateAdUnit, useValidateAdUnitNameUnicity } from '~/api/pbs/ad-units.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { EditPageWrapper, PageWrapper } from '~/components/_styled/page'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { Icon } from '~/components/Icon'
import { BaseInputElement, Input } from '~/components/Input'
import { Link } from '~/components/Link'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import { useDebouncedAsyncValidation } from '~/hooks/asyncValidation'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { AdFormatDisplay, AMP_AD_FORMATS_DISPLAY, INAPP_AD_FORMATS_DISPLAY, useAdFormat } from '~/modules/adm/inventory/adunits/AdFormatDisplay'
import { AdFormatCard } from '~/modules/adm/inventory/adunits/components/AdFormatCard'
import { ErrorMessage } from '~/modules/adm/inventory/adunits/PureAdmAdUnitEditPage'
import { WithClassName } from '~/types/utils'
import { SummaryCard } from './SummaryCard'

const TitleBar = styled.div`
  display: flex;
  padding-bottom: 40px;
  width: 100%;
  justify-content: space-between;
  h1 {
    ${Fonts.H1}
  }
  ${Link} {
    margin-bottom: 12px;
  }
`

const InAppEditContentCard = styled(ContentCard)`
  width: 1000px;
`
const CardActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: end;
`

const CardTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
`

const CardTitle = styled.h2`
  ${Fonts.colors.Jet}
  ${Fonts.H1}
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  margin: 0;
  flex: 1;
`

const BottomBar = styled.div`
  border-top: 1px solid ${Colors.Platinum};
  margin-top: 20px;
`

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FormWrapper = styled.div`
  padding: 20px 0;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  ${Fonts.P1}
  h2 {
    ${Fonts.H2}
    font-weight: 500;
    gap: 4px;
    display: flex;
    align-items: center;
  }
  ${Tooltip} {
    display: inline-flex;
  }
`

const AdUnitIdentification = styled.div`
  display: flex;
  gap: 20px;
  ${BaseInputElement} {
    flex-basis: 304px;
  }
`

const AdFormatTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 500;
`

const AdFormatList = styled.div`
  display: flex;
  flex-direction: row;
  row-gap: 14px;
  column-gap: 16px;
  flex-wrap: wrap;
  :not(:last-child) {
    margin-bottom: 16px;
  }
`

const AdFormatCardWrapper = styled(AdFormatCard)`
  flex-basis: auto;
  min-width: 234px;
`

type AdFormatCategory = {
  name: string
  list: AdFormatDisplay[]
}

const FilterActions = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  ${BaseInputElement} {
    width: 468px;
  }
`

const AdFormatCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ModalContent = styled.p`
  margin-top: 16px;
  width: 550px;
`

const AdUnitType = styled.div`
  margin-bottom: 1.25rem;
`

export type InAppAdUnitForm = PbsAdUnit & {
  bannerEnabled: boolean
}

const emptyAdunit: PbsAdUnit = {
  id: '',
  name: '',
  enabled: true,
  env: 'inapp',
  updatedAt: new Date(),
  updatedBy: '',
  scopeId: '',
}
const SEARCH_DEBOUNCE_TIME = 300 // ms
const mediatypesOptions: SelectOptionProp[] = [
  {
    label: 'All mediatypes',
    value: 'all',
    trackingLabel: `pbs-inapp-ad-units-format-no-filter-apply`,
  },
  {
    label: 'Banner',
    value: 'Banner',
    trackingLabel: 'pbs-inapp-ad-units-format-banner-filter-apply',
  },
  {
    label: 'Outstream',
    value: 'Outstream',
    trackingLabel: 'pbs-inapp-ad-units-format-outstream-filter-apply',
  },
  {
    label: 'Native',
    value: 'Native',
    trackingLabel: 'pbs-inapp-ad-units-format-native-filter-apply',
  },
]

type FormatChangeModalProps = { onClickPrimary: () => unknown }
const FormatChangeModal: FunctionComponent<FormatChangeModalProps> = ({ onClickPrimary }) => {
  const modal = useGlobalModal()

  const close = () => {
    modal.close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Edit format</Modal.Title>

      <Modal.Body>
        <p>You are about to change the format of your ad unit.</p>
        <ModalContent>Don’t forget to change the code in your app. Find your ad unit’s code by clicking its row in the table.</ModalContent>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'pbs-inapp-ad-units-format-change-cancel'}>
          Keep current format
        </Button>
        <Button
          trackingLabel={'pbs-inapp-ad-units-format-change-confirm'}
          onClick={() => {
            onClickPrimary()
            close()
          }}
        >
          Change format
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

type EditAdUnitsPageProps = WithClassName & {
  isEditing: boolean
}

const adFormatCategoriesByEnv = (env: AdUnitEnv) => {
  if (env === 'amp') return AMP_AD_FORMATS_DISPLAY
  else return INAPP_AD_FORMATS_DISPLAY
}

export const EditAdUnitsPage: FunctionComponent<EditAdUnitsPageProps> = ({ isEditing, className }) => {
  const { adUnitId, env } = useParams()
  const { data: currentAdUnit, isLoading } = useAdUnit(adUnitId || '')
  const navigate = useNavigate()
  const toast = useToast()

  const adUnit = isEditing && currentAdUnit ? currentAdUnit : emptyAdunit
  const currentEnv: AdUnitEnv = env && isValidAdUnitEnv(env) ? env : adUnit.env
  const breadcrumbs = useBreadcrumbs(adUnit)
  const validateNameUnique = useValidateAdUnitNameUnicity(currentEnv)

  const debouncedValidateNameUnique = useDebouncedAsyncValidation(validateNameUnique, SEARCH_DEBOUNCE_TIME)

  const {
    control,
    formState: { errors, isValid, isDirty },
    getValues,
    setValue,
    handleSubmit,
    reset,
  } = useForm<InAppAdUnitForm>({
    defaultValues: adUnit,
    mode: 'onChange',
  })

  const modal = useGlobalModal()
  const { adFormat, name: watchedAdUnitName } = useWatch({ control })
  const selectedAdFormat = useAdFormat(adFormat?.id)
  const { control: searchControl } = useForm<{ search: string; filteredMediatype: Mediatype | 'all' }>({
    defaultValues: {
      search: '',
      filteredMediatype: 'all',
    },
  })
  const search = useWatch({ control: searchControl, name: 'search' })
  const filteredMediatype = useWatch({ control: searchControl, name: 'filteredMediatype' })

  useEffect(() => {
    reset(adUnit)
  }, [adUnit])

  const { mutateAsync: updateAdUnit, isPending: isAdUnitUpdatePending } = useUpdateAdUnit(adUnit.id, {
    successToast: (
      <>
        <strong>{adUnit.name}</strong> has been updated.
      </>
    ),
    errorToast: 'An error occurred while saving this adUnit.',
  })
  const { mutateAsync: createAdUnit, isPending: isAdUnitCreatePending } = useCreateAdUnit({
    successToast: (
      <>
        <strong>{watchedAdUnitName}</strong> has been added to your inventory.
      </>
    ),
  })

  async function onCreateAdUnit(adUnit: PbsAdUnit) {
    if (isAdUnitUpdatePending) return
    await createAdUnit(adUnit)
    navigate(-1)
  }

  async function onUpdateAdUnit(adUnit: PbsAdUnit) {
    if (isAdUnitCreatePending) return
    await updateAdUnit(adUnit)
    navigate(-1)
  }

  const onSubmit: SubmitHandler<InAppAdUnitForm> = async (form) => {
    try {
      // `bannerEnabled` drives the format cards on this form; it is not part of the ad unit.
      const { bannerEnabled: _bannerEnabled, ...adUnit } = form
      const adUnitToSubmit = { ...adUnit, env: currentEnv }
      isEditing ? await onUpdateAdUnit(adUnitToSubmit) : await onCreateAdUnit(adUnitToSubmit)
    } catch (e: any) {
      toast.alert(e?.description?.message || e?.message || 'An error occurred while saving this adUnit.')
    }
  }

  const filterAdFormats = (adFormats: AdFormatDisplay[]): AdFormatDisplay[] => {
    const adFormatsFilteredByMediatype = filteredMediatype === 'all' ? adFormats : adFormats.filter((adFormat) => adFormat.mediatypes.includes(filteredMediatype))
    return adFormatsFilteredByMediatype.filter((adFormat) => adFormat.name.toLowerCase().includes(search.toLowerCase()))
  }

  const adFormatCategories: AdFormatCategory[] = [
    {
      name: 'Display',
      list: filterAdFormats(adFormatCategoriesByEnv(currentEnv)),
    },
  ]

  const toggleFormat = (onChange: (...event: any[]) => void, adFormatDisplay: AdFormatDisplay) => {
    onChange(adFormatDisplay)
    setValue('bannerEnabled', adFormatDisplay.mediatypes.includes('Banner'))
  }

  return (
    <EditPageWrapper className={className}>
      <TitleBar>
        {breadcrumbs}
        <Button
          variant={'tertiary'}
          onClick={() => {
            navigate(-1)
          }}
          trackingLabel={'pbs-inapp-forms-back'}
        >
          Back
        </Button>
      </TitleBar>
      <PageWrapper>
        <InAppEditContentCard color={Colors.Turquoise}>
          <CardTitleWrapper>
            <CardTitle>{isLoading ? <Skeleton bigger width={'120px'} /> : isEditing ? `Edit ${adUnit.name}` : 'Ad unit'}</CardTitle>
          </CardTitleWrapper>
          <BottomBar />
          <FormWrapper>
            <FormHeader>
              <h2>Identification</h2>
            </FormHeader>
            <AdUnitIdentification>
              <Input
                type={'text'}
                label={'Name'}
                control={control}
                name={'name'}
                error={errors.name?.message}
                disabled={isEditing}
                rules={{
                  required: { value: true, message: 'Please fill this to continue.' },
                  pattern: { value: noLeadingAndTrailingSpace, message: 'No leading or trailing space allowed.' },
                  validate: {
                    uniqueName: async (value) => {
                      if (isEditing) {
                        return true
                      }
                      if (typeof value === 'string') {
                        const validateName = await debouncedValidateNameUnique(value)
                        if (!validateName) {
                          return 'This name is already used.'
                        }
                        return true
                      }
                      return false
                    },
                  },
                }}
              />
            </AdUnitIdentification>
          </FormWrapper>
          <FormWrapper>
            <FormHeader>
              <h2>Format</h2>
              Select one format.
            </FormHeader>
            <div>
              <AdUnitType>
                <FilterActions>
                  <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={searchControl} />
                  <Select name={'filteredMediatype'} options={mediatypesOptions} label={''} control={searchControl} />
                </FilterActions>
                <Controller
                  control={control}
                  name={'adFormat'}
                  rules={{
                    required: 'Select at least one ad format.',
                  }}
                  render={({ field: { onChange } }) => (
                    <AdFormatCategoryWrapper>
                      {adFormatCategories.map((adFormatCategory) => (
                        <div key={adFormatCategory.name}>
                          <AdFormatTitle>{adFormatCategory.name}</AdFormatTitle>
                          <AdFormatList>
                            {adFormatCategory.list.map((adFormatDisplay) => (
                              <AdFormatCardWrapper
                                key={adFormatDisplay.name}
                                adFormat={adFormatDisplay}
                                isSelected={selectedAdFormat ? selectedAdFormat.id === adFormatDisplay.id : false}
                                onClick={() => {
                                  if (selectedAdFormat?.id !== adFormatDisplay.id) {
                                    selectedAdFormat && isEditing
                                      ? modal.open(FormatChangeModal, {
                                          onClickPrimary: () => {
                                            toggleFormat(onChange, adFormatDisplay)
                                          },
                                          changedProperty: 'format',
                                        })
                                      : toggleFormat(onChange, adFormatDisplay)
                                  }
                                }}
                                trackingLabel={`pbs-inapp-ad-units-format-${normalizeAsTrackingLabel(adFormatDisplay.id)}-set`}
                              />
                            ))}
                          </AdFormatList>
                        </div>
                      ))}
                      {errors.adFormat && (
                        <ErrorMessage>
                          <Icon name={'alert'} width={'0.875rem'} />
                          {errors.adFormat.message}
                        </ErrorMessage>
                      )}
                    </AdFormatCategoryWrapper>
                  )}
                />
              </AdUnitType>
              <CardActions>
                <Button variant={'tertiary'} onClick={() => navigate(-1)} trackingLabel={'pbs-inapp-ad-units-cancel'}>
                  Cancel
                </Button>
                {isEditing ? (
                  <Button
                    trackingLabel={'pbs-inapp-ad-units-save'}
                    disabled={!isDirty || !isValid || isAdUnitUpdatePending || isAdUnitCreatePending}
                    variant={'primary'}
                    data-ph-capture-attribute-adunit-format={adFormat?.name}
                    onClick={handleSubmit(onSubmit)}
                  >
                    Update
                  </Button>
                ) : (
                  <Button
                    trackingLabel={'pbs-inapp-ad-units-save'}
                    data-ph-capture-attribute-adunit-format={adFormat?.name}
                    variant={'primary'}
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isValid || isAdUnitUpdatePending || isAdUnitCreatePending || isLoading}
                  >
                    Validate
                  </Button>
                )}
              </CardActions>
            </div>
          </FormWrapper>
        </InAppEditContentCard>
        <div>
          <SummaryCard adUnit={getValues()} />
        </div>
      </PageWrapper>
    </EditPageWrapper>
  )
}
